/**
 * Invitation.js
 *
 * The party invitation as a set of boxes, rendered from assets/invitation.md (edit that file to change it).
 * Each box's heading says the main thing, so people can skim the headings and know everything.
 *
 * invitation.md format:
 * - "# Title", then the tagline, then the intro (lines up to the first box)
 * - "## Label | Heading" starts a box; the lines under it are its details.
 *   The label is optional ("## Heading"). Add {full} or {third} at the end for a wide or narrow box (default: half).
 * - "Label: text" lines get a bold label, and links are clickable
 * - "[text](#action)" is a button that runs one of the actions passed in, e.g. [click here](#shareable-pass)
 * - ":icon-name:" at the start of a title or heading shows that lucide.dev icon (add new ones to ICONS)
 */

import React, { createContext, useContext, useEffect, useState } from "react";
import {
    CalendarDays, CarTaxiFront, ConciergeBell, Luggage, MapPin, Martini, MessageCircleReply,
    PartyPopper, Plane, PlaneTakeoff, ShieldAlert, Shirt, TreePalm, TriangleAlert,
} from "lucide-react";
import invitationUrl from "../assets/invitation.md";

const LABEL = /^(.{1,40}?):\s(.+)$/;
const LINK_PATTERN = /(\[[^\]]+\]\(#[a-z-]+\)|https?:\/\/\S+)/;
const ACTION_LINK = /^\[([^\]]+)\]\(#([a-z-]+)\)$/;
const SIZE = /\s*\{(full|third)\}\s*$/;
const ICON = /^:([a-z-]+):\s*/;

// The icons invitation.md can use, by their lucide.dev name
const ICONS = {
    "calendar-days"         :   CalendarDays,
    "car-taxi-front"        :   CarTaxiFront,
    "concierge-bell"        :   ConciergeBell,
    "luggage"               :   Luggage,
    "map-pin"               :   MapPin,
    "martini"               :   Martini,
    "message-circle-reply"  :   MessageCircleReply,
    "party-popper"          :   PartyPopper,
    "plane"                 :   Plane,
    "plane-takeoff"         :   PlaneTakeoff,
    "shield-alert"          :   ShieldAlert,
    "shirt"                 :   Shirt,
    "tree-palm"             :   TreePalm,
    "triangle-alert"        :   TriangleAlert,
};

// Grid spans (6 columns from the sm breakpoint, 1 column below it, like the phone boarding pass)
const SPAN = {
    full    :   "sm:col-span-6",
    half    :   "sm:col-span-3",
    third   :   "sm:col-span-2",
};

const PAPER = "#FBF7EF";
const INK = "#1B2A41";
const MUTED = "#5B6577";
const BOX_SHADOW = "0 6px 18px rgba(27,42,65,0.10)";

// The actions "[text](#action)" links can run, by name
const ActionsContext = createContext({});

/**
 * Text with clickable links and action buttons
 * @param {String} text     -   One line
 */
const Linked = ({ text }) => {
    const actions = useContext(ActionsContext);
    return text.split(LINK_PATTERN).map((part, index) => {
        if (index % 2 === 0) return <React.Fragment key={index}>{part}</React.Fragment>;
        const action = part.match(ACTION_LINK);
        if (!action) return <a key={index} href={part} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 break-all">{part}</a>;
        if (!actions[action[2]]) console.warn(`invitation.md: no action called "${action[2]}"`);
        return <button key={index} type="button" onClick={actions[action[2]]} className="font-bold underline underline-offset-2" style={{ color: INK }}>{action[1]}</button>;
    });
};

/**
 * One line, with a bold label if it has one
 * @param {String} line     -   e.g. 'Date: Friday, 30 October 2026'
 */
const Line = ({ line }) => {
    const match = line.match(LABEL);
    if (!match || match[1].includes("http")) return <Linked text={line} />;
    return (
        <>
            <span className="font-bold" style={{ color: INK }}>{match[1]}:</span> <Linked text={match[2]} />
        </>
    );
};

/**
 * A heading with its icon, e.g. ":map-pin: 14 Bahubali Enclave"
 * @param {String} text     -   Heading text, maybe starting with :icon-name:
 * @param {Number} size     -   Icon size in px
 * @param {String} colour   -   Icon colour
 */
const WithIcon = ({ text, size, colour }) => {
    const name = text.match(ICON)?.[1];
    const Icon = ICONS[name];
    if (name && !Icon) console.warn(`invitation.md: no icon called "${name}" in ICONS`);
    return (
        <span className="inline-flex items-center gap-2">
            {Icon && <Icon size={size} color={colour} strokeWidth={2} className="shrink-0" aria-hidden="true" />}
            <span>{text.replace(ICON, "")}</span>
        </span>
    );
};

/**
 * Splits invitation.md into the intro and the boxes
 * @param {String} text     -   invitation.md
 * @returns {Object}        -   { title, tagline, intro: [line], boxes: [{ label, heading, size, lines }] }
 */
const parseInvitation = (text) => {
    const result = { title: "", tagline: "", intro: [], boxes: [] };
    let box = null;

    text.split("\n").map((line) => line.trim()).forEach((line) => {
        if (line.startsWith("## ")) {
            let header = line.slice(3);
            const size = header.match(SIZE)?.[1] || "half";
            header = header.replace(SIZE, "");
            const [label, heading] = header.includes(" | ") ? header.split(" | ") : [null, header];
            box = { label, heading, size, lines: [] };
            result.boxes.push(box);
        } else if (line.startsWith("# ")) {
            result.title = line.slice(2);
        } else if (!line) {
            return;
        } else if (box) {
            box.lines.push(line);
        } else if (!result.tagline) {
            result.tagline = line;
        } else {
            result.intro.push(line);
        }
    });
    return result;
};

/**
 * One box: a small label, a heading that says it all, and the details
 */
const InfoBox = ({ box, accent }) => {
    const centred = !box.label && box.lines.length === 0;
    return (
        <div
            className={`${SPAN[box.size]} rounded-[18px] px-4 py-4 sm:px-5 sm:py-5 ${centred ? "text-center" : "text-left"}`}
            style={{ background: PAPER, color: INK, boxShadow: BOX_SHADOW }}
        >
            {box.label && (
                <p className="text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: accent }}>{box.label}</p>
            )}
            <h3 className={`${box.label ? "mt-1" : ""} text-lg font-bold leading-snug`}>
                <WithIcon text={box.heading} size={20} colour={accent} />
            </h3>
            {box.lines.length > 0 && (
                <div className="mt-3 flex flex-col gap-2 text-sm leading-relaxed" style={{ color: MUTED }}>
                    {box.lines.map((line, index) => <p key={index}><Line line={line} /></p>)}
                </div>
            )}
        </div>
    );
};

/**
 * @param {String} accent   -   The guest's accent colour
 * @param {Object} actions  -   What "[text](#action)" links run, e.g. { "shareable-pass": () => {} }
 * @returns {JSX.Element}
 */
const Invitation = ({ accent, actions = {} }) => {
    const [text, setText] = useState(null);

    useEffect(() => {
        let ignore = false;
        fetch(invitationUrl)
            .then((response) => response.text())
            .then((body) => { if (!ignore) setText(body); })
            .catch((error) => console.error("Error loading the invitation:", error));
        return () => { ignore = true; };
    }, []);

    if (!text) return null;

    const { title, tagline, intro, boxes } = parseInvitation(text);

    return (
        <ActionsContext.Provider value={actions}>
            <section className="w-full flex flex-col gap-4">
                <div className="rounded-[18px] px-4 py-5 sm:px-6 sm:py-7 text-left" style={{ background: PAPER, color: INK, boxShadow: BOX_SHADOW }}>
                    <h2 className="text-xl md:text-2xl font-bold tracking-[0.12em]" style={{ color: accent }}>
                        <WithIcon text={title} size={24} colour={accent} />
                    </h2>
                    {tagline && <p className="mt-1 text-xs font-medium tracking-[0.16em] uppercase" style={{ color: MUTED }}>{tagline}</p>}
                    {intro.length > 0 && (
                        <div className="mt-4 flex flex-col gap-1 text-[15px] leading-relaxed">
                            {intro.map((line, index) => <p key={index}><Line line={line} /></p>)}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                    {boxes.map((box, index) => <InfoBox key={index} box={box} accent={accent} />)}
                </div>
            </section>
        </ActionsContext.Provider>
    );
};

export default Invitation;
